// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, PageShell } from "@/components/PageShell";
import { CtaSection } from "@/components/CtaSection";
import { TabsSection } from "@/components/TabsSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Images
import heroImage from "@/assets/hero-contact.avif";
import bizubeaconImage from "@/assets/bizubeacon.png";
import analogImage from "@/assets/analog.png";
import altimeterImage from "@/assets/altimeter.png";
import controlImage from "@/assets/control.png";

export const Route = createFileRoute("/$lang/products/on-board-components")({
  head: () => ({
    meta: [
      { title: i18n.t("products/on-board-components:metaTitle", "On-Board Components & Avionics — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/on-board-components:metaDesc",
          "Embedded aerospace hardware including BIZU Space BEACON tracking transmitters, analog-to-digital converters, altimeters, and motor control units."
        ),
      },
      { property: "og:title", content: i18n.t("products/on-board-components:metaTitle", "On-Board Components & Avionics — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/on-board-components:metaOgDesc",
          "High-reliability aerospace electronics developed by BIZU Space for sounding rockets, OTVs, and test stand automation."
        ),
      },
      { property: "og:url", content: "/products/on-board-components" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/on-board-components" }],
  }),
  component: OnBoardComponents,
});

function OnBoardComponents() {
  const { t } = useTranslation("products/on-board-components");

  const tabData = {
    beacon: {
      title: t("tabs.beacon.title", "BIZU Space BEACON"),
      subtitle: t("tabs.beacon.subtitle", "Modular tracking and telemetry beacon"),
      image: bizubeaconImage,
      imagePosition: "left" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.beacon.p1", "BIZU Space BEACON is a modular beacon system developed for tracking, localization and communications in mission-critical operations."),
        t("tabs.beacon.p2", "It was originally designed to support rocket missions requiring data recovery, but its architecture also enables use in other mobile or remote systems."),
      ],
      bullets: [
        t("tabs.beacon.b1", "Rocket tracking and sea recovery operations"),
        t("tabs.beacon.b2", "Flight test telemetry support"),
        t("tabs.beacon.b3", "Remote vehicle localization"),
        t("tabs.beacon.b4", "Recovery operations for experimental aerospace systems"),
      ],
    },
    adc: {
      title: t("tabs.adc.title", "ANALOG-TO-DIGITAL CONVERTERS"),
      buttonLabel: t("tabs.adc.buttonLabel", "Analog-To-Digital Converters"),
      subtitle: t("tabs.adc.subtitle", "High-speed and high-resolution data acquisition modules"),
      image: analogImage,
      imagePosition: "right" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.adc.p1", "BIZU Space has developed two ADC modules: one optimized for high-speed acquisition and another for high-resolution measurement."),
        t("tabs.adc.p2", "Their connection standard is compatible with commercially adopted ADC interfaces, helping reduce switching effort and simplifying integration into existing systems."),
      ],
      bullets: [
        t("tabs.adc.b1", "Flight instrumentation"),
        t("tabs.adc.b2", "Ground-test data acquisition"),
        t("tabs.adc.b3", "Engine and propulsion monitoring"),
        t("tabs.adc.b4", "Industrial and laboratory measurement systems"),
      ],
    },
    altimeter: {
      title: t("tabs.altimeter.title", "ALTIMETER"),
      subtitle: t("tabs.altimeter.subtitle", "Event-detection and flight-monitoring unit for rocket missions"),
      image: altimeterImage,
      imagePosition: "left" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.altimeter.p1", "BIZU Space developed an altimeter specifically for rocket-launch applications, combining altitude measurement, motion detection through sensor fusion, and four output channels for command events."),
        t("tabs.altimeter.p2", "The system is suitable for missions that require autonomous event triggering during flight."),
      ],
      bullets: [
        t("tabs.altimeter.b1", "Sounding rockets"),
        t("tabs.altimeter.b2", "University rocketry teams"),
        t("tabs.altimeter.b3", "Flight event sequencing"),
        t("tabs.altimeter.b4", "Experimental launch vehicles"),
      ],
    },
    control: {
      title: t("tabs.control.title", "CONTROL UNIT"),
      subtitle: t("tabs.control.subtitle", "Distributed control electronics for propulsion and actuation systems"),
      image: controlImage,
      imagePosition: "right" as const,
      variant: "split" as const,
      paragraphs: [
        t("tabs.control.p1", "To support liquid and hybrid propulsion systems, BIZU Space developed a control unit featuring 8 power output channels and 8 PWM channels, with CAN communication protocol and opto-isolated serial TTL."),
        t("tabs.control.p2", "The system is designed for reliable command and control of subsystems operating in demanding test and flight environments."),
      ],
      bullets: [
        t("tabs.control.b1", "Liquid rocket control systems"),
        t("tabs.control.b2", "Hybrid rocket control architectures"),
        t("tabs.control.b3", "Valve and actuator command systems"),
        t("tabs.control.b4", "Ground-support and automation systems"),
      ],
    },
  };

  return (
    <PageShell>
      <PageHero
        title={t("heroTitle", "On-board componentes")}
        description={t("heroDesc", "BIZU Space has developed a range of electronic hardware and embedded software to support its own aerospace programs. These technologies are designed for demanding flight and ground operations and can also be adapted for other aerospace, industrial and research applications.")}
        image={heroImage}
      />

      <TabsSection tabs={tabData} />

      <CtaSection
        image={heroImage}
        title={t("ctaTitle", "On-Board Systems Built for Space Missions")}
        subtitle={t("ctaSubtitle", "Flight-ready onboard components engineered for telemetry, command, control, and mission-critical operations. Designed to deliver reliability, seamless integration, and performance from launch to orbit.")}
        buttonText={t("ctaBtn", "Request Technical Details")}
        href="/contact"
      />
    </PageShell>
  );
}
